/**
 * Custom WorldMapComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component, AfterViewInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { WorldMapComponent as ParentWorldMapComponent } from '@shared-lib/chart-components/world-map/world-map.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { GlobalFilterComponent } from '../../global-components/global-filter/global-filter.component';

@Component({
  selector: 'app-world-map',
  standalone: true,
  imports: [CommonModule, GlobalFilterComponent],
  templateUrl: './world-map.component.html',
  styleUrls: ['./world-map.component.scss']
})
export class WorldMapComponent extends ParentWorldMapComponent implements AfterViewInit {
  private readonly traceTag = '[REP-MAP-DBG]';

  protected override resizeListener: (() => void) | null = null;
  protected override readonly regionBounds: Record<string, { center: [number, number]; zoom: number }> = {
    LATAM: { center: [-65, -15], zoom: 1.8 },
    NACA: { center: [-100, 45], zoom: 1.8 },
    AME: { center: [20, 5], zoom: 1.8 },
    APAC: { center: [120, 5], zoom: 1.6 },
    CHINA: { center: [104.2, 35.9], zoom: 2.5 },
    EUROPE: { center: [15, 54], zoom: 2.3 },
    'Northern Europe': { center: [15, 62], zoom: 2.5 },
    INDIA: { center: [78.9, 22.5], zoom: 3.2 },
    India: { center: [78.9, 22.5], zoom: 3.2 },
  };
  private chartReadyForInteraction = false;
  private latestFilters: any = null;
  private lastApiFilterParams: Record<string, any> = {};
  private lastInactiveData: any[] = [];
  private lastVisibleInactiveData: any[] = [];
  private screen1BaselineMinMetric: number | null = null;
  private screen1BaselineMaxMetric: number | null = null;
  private stableBubbleSizingSignature: string | null = null;
  private currentMinMetric: number | null = null;
  private currentMaxMetric: number | null = null;
  private lastActiveRepowerData: any[] = [];
  private legendPercentages: Record<'high' | 'medium' | 'low' | 'inactive' | 'active', number | null> = {
    high: null,
    medium: null,
    low: null,
    inactive: null,
    active: null,
  };
  public readonly activeLegendFireIcon = this.buildActiveLegendFireIcon();
  private readonly activeRepowerFireSymbol = this.buildActiveRepowerFireSymbol();
  private readonly fireLegendGradient = 'linear-gradient(180deg, #ffe082 0%, #ff9f1c 55%, #ef6c00 100%)';
  private readonly riskPalette: Record<'high' | 'medium' | 'low' | 'inactive' | 'active', string> = {
    high: '#ff5b5b',
    medium: '#f0b70d',
    low: '#1db322',
    inactive: '#c2cad8',
    active: '#ff8a00'
  };
  public activeRiskFilter: 'high' | 'medium' | 'low' | 'inactive' | 'active' | null = null;
  public legendItems = [
    { score: 'active', label: 'Active\nRepowers', colorClass: 'active-repower' },
    { score: 'high', label: 'High', colorClass: 'high-risk' },
    { score: 'medium', label: 'Medium', colorClass: 'medium-risk' },
    { score: 'low', label: 'Low', colorClass: 'low-risk' },
    { score: 'inactive', label: 'Inactive', colorClass: 'inactive-risk' }
  ];




  constructor(
    configService: ConfigService,
    globalFilterService: GlobalFilterService,
    private httpClient: HttpClient,
    private routerNav: Router,
    chartFilterHandler: ChartFilterHandlerService,
  ) {
    super(configService, globalFilterService, httpClient, routerNav, chartFilterHandler);
  }

  private getInactiveMinesCacheKey(): string {
    const workflow = String(this.data?.workflowId || this.data?.customPayload?.workflowId || 'default');
    const entity = String(this.data?.entity || this.data?.customPayload?.entity || 'mines');
    return `repower_inactive_mines:${workflow}:${entity}`;
  }

  private persistInactiveMinesCache(data: any[]): void {
    try {
      if (!Array.isArray(data) || data.length === 0) return;
      sessionStorage.setItem(this.getInactiveMinesCacheKey(), JSON.stringify(data));
    } catch (_e) {
      // ignore cache write issues
    }
  }

  private readInactiveMinesCache(): any[] {
    try {
      const raw = sessionStorage.getItem(this.getInactiveMinesCacheKey());
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  override ngOnDestroy(): void {
    // Disable interaction to prevent clicks after destroy
    this.chartReadyForInteraction = false;

    // Clean up resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
    // Call parent ngOnDestroy
    super.ngOnDestroy();
  }

  private darkenColor(color: string, amount: number): string {
    return '#' + color.replace(/^#/, '').replace(/../g, c =>
      ('0' + Math.min(255, Math.max(0, parseInt(c, 16) - amount)).toString(16)).slice(-2)
    );
  }

  private buildActiveFireSvgMarkup(): string {
    return `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>
  <defs>
    <linearGradient id='fireOuterGrad' x1='0%' y1='0%' x2='0%' y2='100%'>
      <stop offset='0%' stop-color='#f06a3f'/>
      <stop offset='55%' stop-color='#dc4c20'/>
      <stop offset='100%' stop-color='#b9360f'/>
    </linearGradient>
    <linearGradient id='fireMidGrad' x1='0%' y1='0%' x2='0%' y2='100%'>
      <stop offset='0%' stop-color='#ffb300'/>
      <stop offset='100%' stop-color='#f57c00'/>
    </linearGradient>
    <linearGradient id='fireCoreGrad' x1='0%' y1='0%' x2='0%' y2='100%'>
      <stop offset='0%' stop-color='#ffe082'/>
      <stop offset='100%' stop-color='#ffca28'/>
    </linearGradient>
  </defs>
  <ellipse cx='19' cy='16' rx='1.8' ry='2.8' fill='#c74a25'/>
  <ellipse cx='30' cy='10' rx='2.2' ry='3.4' fill='#bc3d19'/>
  <ellipse cx='41' cy='13' rx='1.7' ry='2.7' fill='#cb4f28'/>
  <ellipse cx='49' cy='18' rx='1.8' ry='2.8' fill='#b23b19'/>
  <path d='M33 6c2.1 6.4-.5 10.3-3.1 13.4-3 3.7-6 7.4-6 13.2 0 9.5 7.7 17.2 17.2 17.2s17.2-7.7 17.2-17.2c0-7-3.8-12.8-8.4-18.1-2.3-2.7-4.5-5.2-5.3-8.5-3.7 1.6-6.2 4.7-6.2 8.8 0 2.7 1 4.8 2.6 6.5-4-.5-8-3.7-8-10.3z' fill='url(#fireOuterGrad)' stroke='#8f2c12' stroke-width='1.2'/>
  <path d='M41 52c6.1 0 11.1-5 11.1-11.1 0-4.7-2.6-8.7-5.8-12.3-1.8-2.1-3.6-4.2-4.1-6.9-2.4 1.2-4 3.4-4 6.5 0 1.8.6 3.3 1.7 4.7-2.9-.4-5.9-2.5-5.9-7.1-2.2 4.9-6.9 8.3-6.9 14.2C27.1 47 33 52 41 52z' fill='url(#fireMidGrad)'/>
  <path d='M40.7 49.7c3.4 0 6.1-2.8 6.1-6.2 0-2.4-1.1-4.4-2.5-6.3-.9-1.1-1.8-2.3-2-3.8-1.5.9-2.5 2.2-2.5 4.1 0 1 .3 2 .9 2.8-1.7-.3-3.6-1.6-3.6-4.5-1.4 3.2-4.1 5.6-4.1 9.4 0 2.4 1.8 4.5 4.2 4.5z' fill='url(#fireCoreGrad)'/>
</svg>`;
  }

  private buildActiveLegendFireIcon(): string {
    const svg = this.buildActiveFireSvgMarkup();
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  private buildActiveRepowerFireSymbol(): string {
    const svg = this.buildActiveFireSvgMarkup();
    return `image://data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  private toNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const parsed = Number(value.toString().replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getScatterOffset(index: number, radiusLimit: number): [number, number] {
    if (!Number.isFinite(index) || index <= 0) return [0, 0];

    const maxRadius = Math.max(0.8, Number(radiusLimit) || 0.8);
    const spokes = 10;
    const ring = Math.floor((index - 1) / spokes) + 1;
    const spoke = (index - 1) % spokes;
    const angularShift = ring % 2 === 0 ? Math.PI / spokes : 0;
    const angle = (2 * Math.PI * spoke) / spokes + angularShift;
    const radius = Math.min(maxRadius, 0.9 + (ring - 1) * 0.8);

    return [Math.cos(angle) * radius, Math.sin(angle) * radius];
  }

  private getCoordsFromRow(row: any, latKey?: string, lngKey?: string): [number, number] | null {
    if (!row || typeof row !== 'object') return null;
    const normalizedLatKey = typeof latKey === 'string' ? latKey.trim() : '';
    const normalizedLngKey = typeof lngKey === 'string' ? lngKey.trim() : '';
    const lon = this.toNumber(
      (normalizedLngKey ? row[normalizedLngKey] : undefined) ??
      row.longitude ??
      row.lon ??
      row.lng ??
      row.long
    );
    const lat = this.toNumber(
      (normalizedLatKey ? row[normalizedLatKey] : undefined) ??
      row.latitude ??
      row.lat
    );

    if (lon === null || lat === null) return null;
    if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return null;
    return [lon, lat];
  }

  private getScatterGroupingKey(
    center: [number, number],
    anchors: Array<{ key: string; lon: number; lat: number }>,
    proximityThresholdDeg: number
  ): string {
    const [lon, lat] = center;
    const key = `${lon.toFixed(4)},${lat.toFixed(4)}`;
    if (!Number.isFinite(proximityThresholdDeg) || proximityThresholdDeg <= 0) {
      anchors.push({ key, lon, lat });
      return key;
    }

    const proximitySquared = proximityThresholdDeg * proximityThresholdDeg;
    for (const anchor of anchors) {
      const dx = lon - anchor.lon;
      const dy = lat - anchor.lat;
      if ((dx * dx) + (dy * dy) <= proximitySquared) {
        return anchor.key;
      }
    }

    anchors.push({ key, lon, lat });
    return key;
  }

  private getRiskBucket(score: number | null | undefined): 'low' | 'medium' | 'high' {
    const val = Number(score);
    if (!Number.isFinite(val)) return 'low';
    if (val >= 5) return 'high';
    if (val > 3) return 'medium';
    return 'low';
  }

  private normalizeOwnerType(value: any): string {
    if (value === null || value === undefined) return '';
    return value.toString().trim().toLowerCase();
  }

  protected override getMineRisk(mine: any) {
    const rawScore = this.toNumber(mine?.repower_score);
    const score = rawScore ?? 0;
    const bucket = this.getRiskBucket(score);

    const labels: Record<'low' | 'medium' | 'high' | 'inactive', string> = {
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      inactive: 'Inactive'
    };

    return {
      score,
      label: labels[bucket],
      color: this.riskPalette[bucket]
    };
  }

  public getLegendColor(bucket: 'high' | 'medium' | 'low' | 'inactive' | 'active'): string {
    if (bucket === 'active') return this.fireLegendGradient;
    return this.riskPalette[bucket];
  }

  public getLegendValueLabel(bucket: 'high' | 'medium' | 'low' | 'inactive' | 'active'): string {
    if (bucket === 'inactive') return '';
    const value = this.legendPercentages[bucket];
    if (value === null || !Number.isFinite(value)) return '--';
    return `${value.toFixed(2)}%`;
  }

  private getCurrentGlobalFilters(): any {
    try {
      return (this as any).globalFilterService?.getCurrentFilters?.() || null;
    } catch (_e) {
      return null;
    }
  }

  private getFilterValueByAliases(source: any, aliases: string[]): any {
    if (!source || typeof source !== 'object') return undefined;

    for (const alias of aliases) {
      if (alias in source) {
        return source[alias];
      }
    }

    const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');
    const aliasSet = new Set(aliases.map((alias) => normalizeKey(alias)));
    for (const key of Object.keys(source)) {
      if (aliasSet.has(normalizeKey(key))) {
        return source[key];
      }
    }

    return undefined;
  }

  private getBubbleSizeFromMetric(metric: number | null, minMetric: number, maxMetric: number): number | null {
    if (metric === null || !Number.isFinite(metric)) return null;

    const minSize = this.data?.minBubbleSize ?? 20;

    const maxSize = this.data?.maxBubbleSize ?? 60;

    if (!Number.isFinite(minMetric) || !Number.isFinite(maxMetric) || maxMetric <= minMetric) {
      return Math.round((minSize + maxSize) / 2);
    }

    const normalized = (metric - minMetric) / (maxMetric - minMetric);
    const clamped = Math.max(0, Math.min(1, normalized));
    return Math.round(minSize + clamped * (maxSize - minSize));
  }

  private shouldUseStableBubbleSizing(): boolean {
    const raw =
      this.data?.stableBubbleSizing ??
      this.data?.chartConfig?.stableBubbleSizing ??
      this.data?.customPayload?.stableBubbleSizing;

    if (raw !== undefined && raw !== null) {
      if (typeof raw === 'string') {
        return raw.toLowerCase() === 'true';
      }

      return Boolean(raw);
    }

    const currentUrlForSizing = (this.routerNav?.url || '').toLowerCase();
    return currentUrlForSizing.includes('repower-predictor') || currentUrlForSizing.includes('repower-screen-');
  }

  private getNavigateTo(): string | null {
    return this.data?.navigateTo || this.data?.customPayload?.navigateTo || null;
  }

  private getRegionNavigateTo(): string | null {
    return this.data?.regionNavigateTo || this.data?.customPayload?.regionNavigateTo || null;
  }

  private isRepowerScreen2Payload(): boolean {
    const currentUrl = (this.routerNav?.url || '').toLowerCase();
    return this.data?.widgetId === 'world-map-2' || currentUrl.includes('repower-screen-2');
  }

  private getSelectedMineNameForPayload(): string {
    const selectedMine = this.data?.selectedMine;
    return `${this.data?.selectedMineName ||
      selectedMine?.name ||
      selectedMine?.__mineRaw ||
      selectedMine?.mine ||
      (typeof selectedMine === 'string' ? selectedMine : '')}`.trim();
  }

  private shouldUseSelectedMineMapPayload(): boolean {
    const currentUrl = (this.routerNav?.url || '').toLowerCase();
    const functionName = this.data?.functionName || this.data?.customPayload?.functionName || '';
    return currentUrl.includes('repower-screen-3') &&
      functionName === 'MineByCountryReadService' &&
      !!this.getSelectedMineNameForPayload();
  }

  private toPrimitiveFilterValue(value: any): any {
    if (value === null || value === undefined || value === '') return value;
    if (Array.isArray(value)) return value.map((item) => this.toPrimitiveFilterValue(item));
    if (typeof value !== 'object') return value;
    if ('value' in value) return (value as any).value;
    if ('key' in value) return (value as any).key;
    if ('id' in value) return (value as any).id;
    if ('label' in value) return (value as any).label;
    return value;
  }

  private toNestedFilterValue(value: any): any[] {
    const normalized = this.toPrimitiveFilterValue(value);
    if (Array.isArray(normalized)) {
      if (normalized.length > 0 && Array.isArray(normalized[0])) {
        return normalized as any[];
      }
      return [normalized];
    }
    return [[normalized]];
  }

  private buildNormalizedRequestForApi(filters?: any): { dataForApi: any; filtersForApi: any } {
    if (!this.isRepowerScreen2Payload()) {
      const filtersForApi = {
        ...(filters || {}),
        requestParams: { ...((filters && filters.requestParams) || {}) },
      };

      const normalizedFilterParams: any = {};
      Object.keys(filtersForApi.requestParams).forEach((key) => {
        const value = filtersForApi.requestParams[key];
        if (value === null || value === undefined || value === '') return;
        if (Array.isArray(value) && value.length === 0) return;
        normalizedFilterParams[key] = this.toNestedFilterValue(value);
      });
      this.lastApiFilterParams = { ...normalizedFilterParams };

      const existingFilter = this.data?.customPayload?.filter || {};
      const selectedMineName = this.getSelectedMineNameForPayload();
      const useSelectedMineMapPayload = this.shouldUseSelectedMineMapPayload();
      const dataForApi = {
        ...this.data,
        customPayload: {
          ...(this.data?.customPayload || {}),
          filter: useSelectedMineMapPayload
            ? {
                ...existingFilter,
                mine: this.toNestedFilterValue(selectedMineName),
              }
            : {
                ...existingFilter,
                ...normalizedFilterParams,
              },
        },
      };

      // Prevent duplicate top-level requestData keys (region/country/mine) in screen-1 payload.
      filtersForApi.requestParams = {};

      return { dataForApi, filtersForApi };
    }

    const dataForApi = {
      ...this.data,
      // Keep this widget's navigation config in component state only, not in request payload.
      customPayload: {},
    };

    const filtersForApi = {
      ...(filters || {}),
      requestParams: { ...((filters && filters.requestParams) || {}) },
    };

    Object.keys(filtersForApi.requestParams).forEach((key) => {
      const value = filtersForApi.requestParams[key];
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value) && value.length === 0) return;
      filtersForApi.requestParams[key] = this.toNestedFilterValue(value);
    });
    this.lastApiFilterParams = { ...(filtersForApi.requestParams || {}) };

    if (!('region' in filtersForApi.requestParams)) {
      const selectedRegion = this.data?.selectedRegion;
      if (selectedRegion) {
        filtersForApi.requestParams.region = this.toNestedFilterValue(selectedRegion);
      }
    }

    return { dataForApi, filtersForApi };
  }

  private isWorldMapPayloadNode(node: any): boolean {
    if (!node || typeof node !== 'object') return false;
    const uiDataKey = this.data?.uiDataKey || this.data?.customPayload?.uiDataKey || 'ui-data';
    const inactiveMinesKey = this.data?.inactiveMinesDataKey || this.data?.customPayload?.inactiveMinesDataKey || 'inactivemines';
    const activeMinesKey = this.data?.activeMinesDataKey || this.data?.customPayload?.activeMinesDataKey || 'activemines';

    return Boolean(
      Array.isArray(node[uiDataKey]) ||
      Array.isArray(node['ui-data']) ||
      Array.isArray(node[inactiveMinesKey]) ||
      Array.isArray(node['inactivemines']) ||
      Array.isArray(node[activeMinesKey]) ||
      Array.isArray(node['activemines']) ||
      Array.isArray(node['filter-data'])
    );
  }

  private extractWorldMapResponseList(raw: any): any[] {
    if (!raw || typeof raw !== 'object') return [];

    if (Array.isArray(raw.responseList) && raw.responseList.some((item: any) => this.isWorldMapPayloadNode(item))) {
      return raw.responseList;
    }

    if (this.isWorldMapPayloadNode(raw)) {
      return [raw];
    }

    let current: any = raw;
    const visited = new Set<any>();

    while (current && typeof current === 'object' && !visited.has(current)) {
      visited.add(current);

      const responseList = Array.isArray(current.responseList) ? current.responseList : [];
      if (responseList.length === 0) break;

      const directMatch = responseList.filter((item: any) => this.isWorldMapPayloadNode(item));
      if (directMatch.length > 0) {
        return directMatch;
      }

      const first = responseList[0];
      if (first && Array.isArray(first.data)) {
        const dataMatch = first.data.filter((item: any) => this.isWorldMapPayloadNode(item));
        if (dataMatch.length > 0) {
          return dataMatch;
        }
        current = first.data[0];
        continue;
      }

      current = first;
    }

    return Array.isArray(raw.responseList) ? raw.responseList : [];
  }

  private normalizeWorldMapResponse(raw: any): any {
    const responseList = this.extractWorldMapResponseList(raw);
    if (!responseList.length) return raw;

    if (Array.isArray(raw?.responseList) && raw.responseList === responseList) {
      return raw;
    }

    return {
      ...(raw && typeof raw === 'object' ? raw : {}),
      responseList,
    };
  }

  private updateLegendPercentages(raw: any): void {
    this.legendPercentages = {
      high: null,
      medium: null,
      low: null,
      inactive: null,
      active: null,
    };

    const responseList = this.extractWorldMapResponseList(raw);
    const repowerPercentages = responseList?.[0]?.['repower-percentage']?.[0]?.data;
    if (!repowerPercentages || typeof repowerPercentages !== 'object') {
      return;
    }

    // High risk -> red, Medium risk -> yellow, Low risk -> green
    this.legendPercentages.high = this.toNumber(repowerPercentages.red_percentage);
    this.legendPercentages.medium = this.toNumber(repowerPercentages.yellow_percentage);
    this.legendPercentages.low = this.toNumber(repowerPercentages.green_percentage);
    this.legendPercentages.inactive = this.toNumber(repowerPercentages.inactive_percentage);
    this.legendPercentages.active = this.toNumber(repowerPercentages.active_repowers_percentage);
  }

  protected override fetchData(filters?: any): void {
    const globalFilters = this.getCurrentGlobalFilters() || {};
    const effectiveFilters = {
      ...globalFilters,
      ...(filters || {}),
      requestParams: {
        ...((globalFilters && globalFilters.requestParams) || {}),
        ...((filters && filters.requestParams) || {}),
      },
      queryParams: {
        ...((globalFilters && globalFilters.queryParams) || {}),
        ...((filters && filters.queryParams) || {}),
      },
    };
    this.latestFilters = effectiveFilters;
    console.log(`${this.traceTag} WorldMap.fetchData called.`, {
      widgetId: this.data?.widgetId,
      filters: effectiveFilters,
      navigateTo: this.getNavigateTo(),
    });

    // Disable interaction until new chart is rendered
    this.chartReadyForInteraction = false;

    const { dataForApi, filtersForApi } = this.buildNormalizedRequestForApi(effectiveFilters);
    (this as any).configService.getConfig(this.data.apiUrl, dataForApi, filtersForApi, this.data.method).subscribe({
      next: (res: any) => {
        console.log(`${this.traceTag} WorldMap.fetchData raw response received.`, {
          widgetId: this.data?.widgetId,
          topLevelKeys: res && typeof res === 'object' ? Object.keys(res).slice(0, 12) : [],
          responseListLength: Array.isArray(res?.responseList) ? res.responseList.length : 0,
        });
        const normalizedResponse = this.normalizeWorldMapResponse(res);
        const normalizedResponseList = this.extractWorldMapResponseList(normalizedResponse);
        console.log(`${this.traceTag} WorldMap.fetchData normalized response.`, {
          widgetId: this.data?.widgetId,
          responseListLength: normalizedResponseList.length,
          responseKeys: normalizedResponseList.map((item: any) =>
            item && typeof item === 'object' ? Object.keys(item).slice(0, 10) : []
          ),
        });
        this.updateLegendPercentages(normalizedResponse);
        try {
          (this as any).loadRiskFromResponse(normalizedResponse);
        } catch (_e) {
          console.warn('[CustomWorldMap] loadRiskFromResponse failed:', _e);
        }
        this.activeRiskFilter = null; // Reset filter on new data
        this.renderMapChart(normalizedResponse);
      },
      error: (err: any) => {
        console.error('[CustomWorldMap] API Error:', err);
        (this as any).chart?.hideLoading?.();
      }
    });
  }

  protected renderMapChart(chartData?: any): void {
    if (!this.data) return;

    const isStatic = this.data?.static === true || this.data?.isStatic === true;
    const series: any[] = [];
    const bubbleEnabled = this.data?.bubble === true;
    const responseList = this.extractWorldMapResponseList(chartData);
    const uiDataKey = this.data?.uiDataKey || this.data?.customPayload?.uiDataKey || 'ui-data';
    const inactiveMinesDataKey = this.data?.inactiveMinesDataKey || this.data?.customPayload?.inactiveMinesDataKey || 'inactivemines';
    const activeMinesDataKey = this.data?.activeMinesDataKey || this.data?.customPayload?.activeMinesDataKey || 'activemines';
    const fallbackRegionFromState = this.resolveRegionKey(
      this.data?.selectedRegion ||
      this.extractNormalizedFilterValues(this.latestFilters?.requestParams?.region)?.[0] ||
      this.data?.customPayload?.requestData?.region
    );

    if (bubbleEnabled && responseList.length > 0) {
      const bubbleValueKey = this.data?.bubbleValueKey || 'repower_score';
      const bubbleSizeKey = this.data?.bubbleSizeKey || 'engine_population';
      const bubbleLabel = this.data?.bubbleLabel || 'mine';
      const bubbleCountryKey = this.data?.bubbleCountryKey || 'country';
      const bubbleRegionKey = this.data?.bubbleRegionKey || 'region';
      const bubbleLatKey = this.data?.bubbleLatKey || 'latitude';
      const bubbleLngKey = this.data?.bubbleLngKey || 'longitude';
      const bubbleColor = this.data?.bubbleColor || '#ff5722';
      const bubbleScatterRadius = this.data?.bubbleScatterRadius ?? 5.5;
      const scatterGroupingThreshold = Number(this.data?.bubbleScatterGroupingThresholdDeg ?? 0.35);

      const bubbleData: any[] = [];
      const inactiveBubbleData: any[] = [];
      const activeRepowerData: any[] = [];
      const countryBubbleIndex: Record<string, number> = {};
      const scatterAnchors: Array<{ key: string; lon: number; lat: number }> = [];
      const activeFromNavState = Array.isArray(this.data?.activeRepowersSnapshot)
        ? this.data.activeRepowersSnapshot
        : [];
      const inactiveMineColor = this.data?.inactiveMineColor || '#cbd5e1';
      const inactiveMineOpacity = this.data?.inactiveMineOpacity ?? 0.7;
      const activeMineColor = this.data?.activeMineColor || '#ff6a00';
      const activeMineInnerColor = this.data?.activeMineInnerColor || '#ffd54f';
      const activeMineBorderColor = this.data?.activeMineBorderColor || '#a63b00';
      const activeMineIconSize = this.data?.activeMineIconSize ?? 42;
      const ownerTypeByMineNorm = new Map<string, string>();

      for (const resp of responseList) {
        const uiDataList = resp?.[uiDataKey] || resp?.['ui-data'] || [];
        for (const uiData of uiDataList) {
          const rows = uiData.data || [];
          for (const row of rows) {
            const name = row[bubbleLabel];
            const mineNorm = (name || '').toString().toLowerCase().trim();
            const rowRegionRaw = row[bubbleRegionKey] || row['region'] || fallbackRegionFromState;
            const rowRegion = this.resolveRegionKey(rowRegionRaw) || rowRegionRaw;
            const countryFromRow =
              row[bubbleCountryKey] ||
              row['country'] ||
              row['country_name'] ||
              row['countryName'];
            const country = countryFromRow || this.getFallbackCountryForRegion(rowRegion);
            const riskKey = this.data?.riskKey || this.data?.bubbleValueKey || bubbleValueKey;
            const rawValue = row[riskKey];
            const value = typeof rawValue !== 'undefined' && rawValue !== null ? rawValue : (row[bubbleValueKey] ?? 1);
            const ownerTypeNorm = this.normalizeOwnerType(row['owner_type'] || row['ownerType']);

            if (!name) continue;
            if (mineNorm) {
              ownerTypeByMineNorm.set(mineNorm, ownerTypeNorm);
            }

            const center =
              this.getCoordsFromRow(row, bubbleLatKey, bubbleLngKey) ||
              (country ? (this as any).getCountryCenter(country) : null) ||
              this.getRegionCenter(rowRegion);
            if (center) {
              const normCountry = (country || '').toLowerCase().trim();
              const scatterKey = this.getScatterGroupingKey(center, scatterAnchors, scatterGroupingThreshold) || normCountry;
              const idx = countryBubbleIndex[scatterKey] || 0;
              countryBubbleIndex[scatterKey] = idx + 1;

              const [offsetX, offsetY] = this.getScatterOffset(idx, bubbleScatterRadius);
              const finalX = center[0] + offsetX;
              const finalY = center[1] + offsetY;

              const riskMeta = this.getMineRisk({ repower_score: value });
              const color = riskMeta.color || bubbleColor;
              const bubbleSizeMetric = this.toNumber(row[bubbleSizeKey]);
              const darkBorder = this.darkenColor(color, 40);
              const hoverBorder = this.darkenColor(color, 55);

              bubbleData.push({
                name: name,
                value: [finalX, finalY, value, riskMeta.score, bubbleSizeMetric],
                itemStyle: {
                  color: color,
                  borderColor: darkBorder,
                  borderWidth: 1.5
                },
                emphasis: {
                  itemStyle: {
                    borderColor: hoverBorder,
                    borderWidth: 3
                  }
                },
                __risk: riskMeta,
                __countryNorm: normCountry,
                __countryRaw: country || '',
                __regionNorm: (rowRegion || '').toString().toLowerCase().trim(),
                __regionRaw: rowRegion,
                __mineNorm: mineNorm,
                __mineRaw: name,
                __ownerTypeNorm: ownerTypeNorm,
                __ownerTypeRaw: row['owner_type'] || row['ownerType'] || '',
                __engineModel: row['engine_model_key'] || row['engine_model'] || '',
                __bubbleSizeMetric: bubbleSizeMetric,
                __fullRow: row
              });
            }
          }
        }

        // Process inactive mines if enabled
        const excludeInactiveData = this.data?.customPayload?.excludeInactiveData === true;
        const inactiveMinesList = resp?.[inactiveMinesDataKey] || resp?.['inactivemines'];
        if (this.data?.showInactiveMines && !excludeInactiveData && Array.isArray(inactiveMinesList)) {
          const inactiveMineBubbleSize = this.data?.inactiveMineBubbleSize ?? 15;
          for (const inactiveObj of inactiveMinesList) {
            const inactiveMinesRows = inactiveObj.data || [];
            for (const row of inactiveMinesRows) {
              const name = row[bubbleLabel] || row['mine'];
              const rowRegionRaw = row[bubbleRegionKey] || row['region'] || fallbackRegionFromState;
              const rowRegion = this.resolveRegionKey(rowRegionRaw) || rowRegionRaw;
              const inactiveMineNorm = (name || '').toString().toLowerCase().trim();
              const countryFromRow =
                row[bubbleCountryKey] ||
                row['country'] ||
                row['country_name'] ||
                row['countryName'];
              const country = countryFromRow || this.getFallbackCountryForRegion(rowRegion);
              const ownerTypeNorm =
                this.normalizeOwnerType(row['owner_type'] || row['ownerType']) ||
                ownerTypeByMineNorm.get(inactiveMineNorm) ||
                '';

              if (!name) continue;

              const center =
                this.getCoordsFromRow(row, bubbleLatKey, bubbleLngKey) ||
                (country ? (this as any).getCountryCenter(country) : null) ||
                this.getRegionCenter(rowRegion);
              if (center) {
                const normCountry = (country || '').toLowerCase().trim();
                const scatterKey = this.getScatterGroupingKey(center, scatterAnchors, scatterGroupingThreshold) || normCountry;
                const idx = countryBubbleIndex[scatterKey] || 0;
                countryBubbleIndex[scatterKey] = idx + 1;

                const [offsetX, offsetY] = this.getScatterOffset(idx, bubbleScatterRadius);
                const finalX = center[0] + offsetX;
                const finalY = center[1] + offsetY;

                inactiveBubbleData.push({
                  name: name,
                  value: [finalX, finalY, 0, 0, inactiveMineBubbleSize],
                  itemStyle: {
                    color: this.riskPalette.inactive,
                    borderColor: '#FFFFFF',
                    borderWidth: 1,
                    opacity: inactiveMineOpacity
                  },
                  __countryNorm: normCountry,
                  __countryRaw: country || '',
                  __regionNorm: (rowRegion || '').toString().toLowerCase().trim(),
                  __regionRaw: rowRegion,
                  __mineNorm: inactiveMineNorm,
                  __mineRaw: name,
                  __ownerTypeNorm: ownerTypeNorm,
                  __ownerTypeRaw: row['owner_type'] || row['ownerType'] || ownerTypeNorm || '',
                  __bubbleSizeMetric: inactiveMineBubbleSize,
                  __fullRow: row,
                  __isInactive: true
                });
              }
            }
          }
        }

        const activeMinesList = resp?.[activeMinesDataKey] || resp?.['activemines'];
        if (Array.isArray(activeMinesList)) {
          const activeMineScatterRadius = this.data?.activeMineScatterRadius ?? bubbleScatterRadius;
          for (const activeObj of activeMinesList) {
            const activeRows = activeObj?.data || [];
            for (const row of activeRows) {
              const name = row[bubbleLabel] || row['mine'];
              if (!name) continue;

              const mineNorm = (name || '').toString().toLowerCase().trim();
              const rowRegionRaw = row[bubbleRegionKey] || row['region'] || fallbackRegionFromState;
              const rowRegion = this.resolveRegionKey(rowRegionRaw) || rowRegionRaw;
              const countryFromRow =
                row[bubbleCountryKey] ||
                row['country'] ||
                row['country_name'] ||
                row['countryName'];
              const country = countryFromRow || this.getFallbackCountryForRegion(rowRegion);
              const ownerTypeNorm =
                this.normalizeOwnerType(row['owner_type'] || row['ownerType']) ||
                ownerTypeByMineNorm.get(mineNorm) ||
                '';
              const bubbleSizeMetric = this.toNumber(row[bubbleSizeKey]);

              const center =
                this.getCoordsFromRow(row, bubbleLatKey, bubbleLngKey) ||
                (country ? (this as any).getCountryCenter(country) : null) ||
                this.getRegionCenter(rowRegion);
              if (!center) continue;

              const normCountry = (country || '').toLowerCase().trim();
              const scatterGroupKey = this.getScatterGroupingKey(center, scatterAnchors, scatterGroupingThreshold) || normCountry;
              const scatterKey = `${scatterGroupKey}:active`;
              const idx = countryBubbleIndex[scatterKey] || 0;
              countryBubbleIndex[scatterKey] = idx + 1;

              const [offsetX, offsetY] = this.getScatterOffset(idx, activeMineScatterRadius);
              const finalX = center[0] + offsetX;
              const finalY = center[1] + offsetY;

              activeRepowerData.push({
                name: name,
                value: [finalX, finalY, 0, 0, bubbleSizeMetric],
                itemStyle: {
                  color: {
                    type: 'radial',
                    x: 0.45,
                    y: 0.35,
                    r: 0.95,
                    colorStops: [
                      { offset: 0, color: activeMineInnerColor },
                      { offset: 1, color: activeMineColor }
                    ]
                  },
                  borderColor: activeMineBorderColor,
                  borderWidth: 1.5
                },
                __countryNorm: normCountry,
                __countryRaw: country || '',
                __regionNorm: (rowRegion || '').toString().toLowerCase().trim(),
                __regionRaw: rowRegion,
                __mineNorm: mineNorm,
                __mineRaw: name,
                __ownerTypeNorm: ownerTypeNorm,
                __ownerTypeRaw: row['owner_type'] || row['ownerType'] || ownerTypeNorm || '',
                __bubbleSizeMetric: bubbleSizeMetric,
                __fullRow: row,
                __isActiveRepower: true,
                __activeIconSize: activeMineIconSize
              });
            }
          }
        }
      }

      console.log(`${this.traceTag} WorldMap.renderMapChart extracted point sets.`, {
        widgetId: this.data?.widgetId,
        responseListLength: responseList.length,
        bubbleCount: bubbleData.length,
        inactiveCount: inactiveBubbleData.length,
        activeCount: activeRepowerData.length,
        activeMinesDataKey,
        inactiveMinesDataKey,
      });

      // Screen-2 fallback: when API payload does not return "activemines",
      // reuse the active-mine snapshot passed from screen-1 navigation.
      if (activeRepowerData.length === 0 && activeFromNavState.length > 0) {
        activeRepowerData.push(...activeFromNavState.map((point: any) => ({ ...point })));
      }

      const activeMineNormSet = new Set(activeRepowerData.map((point) => (point?.__mineNorm || '').toLowerCase().trim()));
      if (activeMineNormSet.size > 0) {
        const nonActiveBubbles = bubbleData.filter((bubble) =>
          !activeMineNormSet.has((bubble?.__mineNorm || '').toLowerCase().trim())
        );
        bubbleData.length = 0;
        bubbleData.push(...nonActiveBubbles);
      }

      const allBubbleDataBeforeFilter = [...bubbleData];
      const inactiveFromNavState = Array.isArray(this.data?.inactiveMinesSnapshot)
        ? this.data.inactiveMinesSnapshot
        : [];
      const inactiveFromCache = this.readInactiveMinesCache();
      const excludeInactiveData = this.data?.customPayload?.excludeInactiveData === true;

      if (inactiveBubbleData.length > 0 && !excludeInactiveData) {
        this.lastInactiveData = [...inactiveBubbleData];
        this.persistInactiveMinesCache(this.lastInactiveData);
      } else if (inactiveFromNavState.length > 0 && !excludeInactiveData) {
        this.lastInactiveData = [...inactiveFromNavState];
        this.persistInactiveMinesCache(this.lastInactiveData);
      } else if (inactiveFromCache.length > 0 && !excludeInactiveData) {
        this.lastInactiveData = [...inactiveFromCache];
      } else if (excludeInactiveData) {
        this.lastInactiveData = [];
      }

      let visibleInactiveData: any[] = [...(this.lastInactiveData || [])];
      let visibleActiveRepowerData: any[] = [...activeRepowerData];
      const selectedRegions = this.getSelectedRegionsFromFilters();
      // Screen-1 owner/country/mine filtering should not inherit stale region focus from
      // navigation state, otherwise valid global coordinates can be hidden unexpectedly.
      const focusedRegionFromState = this.isScreen1View
        ? null
        : (this.resolveRegionKey(this.data?.selectedRegion) || this.data?.selectedRegion);
      const effectiveRegions = selectedRegions.length > 0
        ? selectedRegions
        : (focusedRegionFromState ? [focusedRegionFromState] : []);

      if (effectiveRegions.length > 0) {
        const selectedNorm = effectiveRegions.map((r) => r.toLowerCase().trim());
        const mappedCountries = this.getCountriesForRegions(selectedNorm);

        const filteredByRegion = bubbleData.filter((bubble) => {
          const bubbleRegion = (bubble.__regionNorm || '').toLowerCase().trim();
          const bubbleCountry = (bubble.__countryNorm || '').toLowerCase().trim();

          if (bubbleRegion && selectedNorm.includes(bubbleRegion)) {
            return true;
          }

          return mappedCountries.has(bubbleCountry);
        });

        bubbleData.length = 0;
        bubbleData.push(...filteredByRegion);

        visibleInactiveData = this.filterInactiveByRegionNorms(selectedNorm, this.lastInactiveData || []);
        visibleActiveRepowerData = visibleActiveRepowerData.filter((bubble) => {
          const bubbleRegion = (bubble?.__regionNorm || '').toLowerCase().trim();
          const bubbleCountry = (bubble?.__countryNorm || '').toLowerCase().trim();
          if (bubbleRegion && selectedNorm.includes(bubbleRegion)) {
            return true;
          }
          return mappedCountries.has(bubbleCountry);
        });
      }

      const selectedCountries = this.getSelectedCountriesFromFilters();
      if (selectedCountries.length > 0) {
        const selectedCountryNorm = selectedCountries.map((country) => country.toLowerCase().trim());
        const filteredByCountry = bubbleData.filter((bubble) => {
          const bubbleCountry = (bubble.__countryNorm || '').toLowerCase().trim();
          return selectedCountryNorm.includes(bubbleCountry);
        });

        bubbleData.length = 0;
        bubbleData.push(...filteredByCountry);

        visibleInactiveData = visibleInactiveData.filter((bubble) => {
          const bubbleCountry = (bubble?.__countryNorm || '').toLowerCase().trim();
          return selectedCountryNorm.includes(bubbleCountry);
        });
        visibleActiveRepowerData = visibleActiveRepowerData.filter((bubble) => {
          const bubbleCountry = (bubble?.__countryNorm || '').toLowerCase().trim();
          return selectedCountryNorm.includes(bubbleCountry);
        });
      }

      const selectedMines = this.getSelectedMinesFromFilters();
      if (selectedMines.length > 0) {
        const selectedMineNorm = selectedMines.map((mine) => mine.toLowerCase().trim());
        const filteredByMine = bubbleData.filter((bubble) => {
          const bubbleMine = (bubble.__mineNorm || '').toLowerCase().trim();
          return selectedMineNorm.includes(bubbleMine);
        });

        bubbleData.length = 0;
        bubbleData.push(...filteredByMine);

        visibleInactiveData = visibleInactiveData.filter((bubble) => {
          const bubbleMine = (bubble?.__mineNorm || '').toLowerCase().trim();
          return selectedMineNorm.includes(bubbleMine);
        });
        visibleActiveRepowerData = visibleActiveRepowerData.filter((bubble) => {
          const bubbleMine = (bubble?.__mineNorm || '').toLowerCase().trim();
          return selectedMineNorm.includes(bubbleMine);
        });
      }

      const selectedOwnerTypes = this.getSelectedOwnerTypesFromFilters();
      if (selectedOwnerTypes.length > 0) {
        const selectedOwnerTypeNorm = new Set(
          selectedOwnerTypes.map((ownerType) => this.normalizeOwnerType(ownerType)).filter((ownerType) => ownerType.length > 0)
        );

        const filteredByOwnerType = bubbleData.filter((bubble) => {
          const bubbleOwnerType = this.normalizeOwnerType(
            bubble?.__ownerTypeNorm ||
            bubble?.__ownerTypeRaw ||
            bubble?.__fullRow?.owner_type ||
            bubble?.__fullRow?.ownerType
          );
          // Keep rows with missing owner type; backend payload is already owner-filtered.
          if (bubbleOwnerType.length === 0) {
            return true;
          }
          return selectedOwnerTypeNorm.has(bubbleOwnerType);
        });

        bubbleData.length = 0;
        bubbleData.push(...filteredByOwnerType);

        visibleInactiveData = visibleInactiveData.filter((bubble) => {
          const bubbleOwnerType = this.normalizeOwnerType(
            bubble?.__ownerTypeNorm ||
            bubble?.__ownerTypeRaw ||
            bubble?.__fullRow?.owner_type ||
            bubble?.__fullRow?.ownerType
          );
          // Inactive payload rows often don't include owner_type; keep them visible.
          if (bubbleOwnerType.length === 0) {
            return true;
          }
          return selectedOwnerTypeNorm.has(bubbleOwnerType);
        });
        visibleActiveRepowerData = visibleActiveRepowerData.filter((bubble) => {
          const bubbleOwnerType = this.normalizeOwnerType(
            bubble?.__ownerTypeNorm ||
            bubble?.__ownerTypeRaw ||
            bubble?.__fullRow?.owner_type ||
            bubble?.__fullRow?.ownerType
          );
          // Active repower payloads can come back without owner_type even when
          // the server has already applied the owner filter. Do not drop them
          // client-side solely because that field is absent.
          if (bubbleOwnerType.length === 0) {
            return true;
          }
          return selectedOwnerTypeNorm.has(bubbleOwnerType);
        });

        console.log(`${this.traceTag} WorldMap.renderMapChart applied owner_type filter.`, {
          widgetId: this.data?.widgetId,
          selectedOwnerTypes,
          visibleBubbleCount: bubbleData.length,
          visibleInactiveCount: visibleInactiveData.length,
          visibleActiveCount: visibleActiveRepowerData.length,
        });
      }

      // On navigation from screen-1 to screen-3, selectedMine arrives via router state.
      // Filter immediately so we don't briefly render global bubbles before zoom animation.
      const selectedMineFromState = this.data?.selectedMine;
      const hasExplicitFilterSelection =
        selectedRegions.length > 0 ||
        selectedCountries.length > 0 ||
        selectedMines.length > 0 ||
        selectedOwnerTypes.length > 0;

      if (selectedMineFromState && !hasExplicitFilterSelection) {
        const mineNameCandidates = [
          selectedMineFromState?.name,
          selectedMineFromState?.__mineRaw,
          selectedMineFromState?.mine
        ]
          .filter((v: any) => !!v)
          .map((v: any) => v.toString().toLowerCase().trim());

        if (mineNameCandidates.length > 0) {
          const hasSelectedActiveRepower = visibleActiveRepowerData.some((bubble) => {
            const bubbleMine = (bubble.__mineNorm || '').toLowerCase().trim();
            return mineNameCandidates.includes(bubbleMine);
          });
          const isSelectedMineInactive = selectedMineFromState.__isInactive === true ||
            !bubbleData.some(bubble => {
              const bubbleMine = (bubble.__mineNorm || '').toLowerCase().trim();
              return mineNameCandidates.includes(bubbleMine);
            });

          if (hasSelectedActiveRepower) {
            bubbleData.length = 0;
            visibleInactiveData = [];
            visibleActiveRepowerData = visibleActiveRepowerData.filter((bubble) => {
              const bubbleMine = (bubble.__mineNorm || '').toLowerCase().trim();
              return mineNameCandidates.includes(bubbleMine);
            });
          } else if (isSelectedMineInactive) {
            if (excludeInactiveData) {
              // Screen-3 can request excludeInactiveData=true; in that case keep API-returned
              // active/ui-data points instead of forcing the stale inactive mine from router state.
              console.log('[CustomWorldMap] Skipping inactive selectedMine override because excludeInactiveData=true');
            } else {
              console.log('[CustomWorldMap] Selected mine is inactive. clearing active bubbleData. selectedMineFromState:', selectedMineFromState);
              bubbleData.length = 0;
              visibleInactiveData = [selectedMineFromState];
              visibleActiveRepowerData = [];
              console.log('[CustomWorldMap] Forced visibleInactiveData:', visibleInactiveData);
            }
          } else {
            const filteredBySelectedMine = bubbleData.filter((bubble) => {
              const bubbleMine = (bubble.__mineNorm || '').toLowerCase().trim();
              return mineNameCandidates.includes(bubbleMine);
            });

            if (filteredBySelectedMine.length > 0) {
              bubbleData.length = 0;
              bubbleData.push(...filteredBySelectedMine);
            }
            visibleInactiveData = [];
            visibleActiveRepowerData = [];
          }
        }
      }

      console.log(
        '[CustomWorldMap] bubbleData length:',
        bubbleData.length,
        'visibleInactiveData length:',
        visibleInactiveData.length,
        'visibleActiveRepowerData length:',
        visibleActiveRepowerData.length
      );

      this.lastVisibleInactiveData = [...visibleInactiveData];
      this.lastActiveRepowerData = [...visibleActiveRepowerData];

      if (bubbleData.length > 0 || visibleInactiveData.length > 0 || visibleActiveRepowerData.length > 0) {
        const useStableBubbleSizing = this.shouldUseStableBubbleSizing();
        const defaultSize = this.data?.minBubbleSize ?? 10;
        // Reuse the full dataset for sizing when stable bubble sizing is enabled
        // so region/country/mine focus doesn't inflate the remaining bubbles.
        const sizingSource = useStableBubbleSizing
          ? allBubbleDataBeforeFilter
          : bubbleData;

        const sizeMetrics = sizingSource
          .map((bubble) => bubble.__bubbleSizeMetric)
          .filter((metric: any) => typeof metric === 'number' && Number.isFinite(metric));

        const stateMin = history.state?.globalMinMetric;
        const stateMax = history.state?.globalMaxMetric;
        const configMin = this.data?.globalMinMetric ?? stateMin;
        const configMax = this.data?.globalMaxMetric ?? stateMax;

        const liveMinMetric = configMin ?? (sizeMetrics.length > 0 ? Math.min(...sizeMetrics) : 0);
        const liveMaxMetric = configMax ?? (sizeMetrics.length > 0 ? Math.max(...sizeMetrics) : 0);
        const currentSizingSignature = useStableBubbleSizing
          ? JSON.stringify(sizeMetrics)
          : null;

        if (useStableBubbleSizing && (
          this.screen1BaselineMinMetric === null ||
          this.screen1BaselineMaxMetric === null ||
          this.stableBubbleSizingSignature !== currentSizingSignature
        )) {
          this.screen1BaselineMinMetric = liveMinMetric;
          this.screen1BaselineMaxMetric = liveMaxMetric;
          this.stableBubbleSizingSignature = currentSizingSignature;
        }

        const minMetric = (useStableBubbleSizing && this.screen1BaselineMinMetric !== null)
          ? this.screen1BaselineMinMetric
          : liveMinMetric;
        const maxMetric = (useStableBubbleSizing && this.screen1BaselineMaxMetric !== null)
          ? this.screen1BaselineMaxMetric
          : liveMaxMetric;

        this.currentMinMetric = minMetric;
        this.currentMaxMetric = maxMetric;

        // Assign render size on the sizing source first so filtered subset reuses
        // exactly the same "no-filter" sizes when stable sizing is enabled.
        sizingSource.forEach((bubble: any) => {
          const metric = bubble.__bubbleSizeMetric;
          const metricSize = this.getBubbleSizeFromMetric(metric, minMetric, maxMetric);
          bubble.__renderSize = metricSize ?? defaultSize;
          // Persist size at data-point level so follow-up setOption patches
          // (zoom/filter) don't accidentally lose/recompute symbol sizing.
          bubble.symbolSize = bubble.__renderSize;
          const renderPriority = 1000 - Math.round(bubble.__renderSize);
          bubble.z = renderPriority;
          bubble.z2 = renderPriority;
        });

        bubbleData.sort((a: any, b: any) => (b.__renderSize || 0) - (a.__renderSize || 0));

        (this as any).bubbleSeriesIndex = series.length;
        (this as any).lastBubbleData = bubbleData;
        if (bubbleData.length > 0) {
          series.push({
            name: this.data?.bubbleTitle || 'Mines',
            type: 'scatter',
            coordinateSystem: 'geo',
            data: bubbleData,
            symbolSize: (val: any, params: any) => {
              const sizeFromPoint = params?.data?.__renderSize;
              if (typeof sizeFromPoint === 'number' && Number.isFinite(sizeFromPoint)) {
                return sizeFromPoint;
              }
              const metric = Array.isArray(val) ? (val[4] ?? null) : null;
              const metricSize = this.getBubbleSizeFromMetric(metric, minMetric, maxMetric);
              return metricSize ?? defaultSize;
            },
            label: { show: false },
            itemStyle: {
              color: bubbleColor,
              borderColor: '#333',
              borderWidth: 1
            },
            emphasis: {
              itemStyle: {
                borderWidth: 3,
                borderColor: this.darkenColor(bubbleColor, 55)
              }
            },
            zlevel: 1
          });
        }

        // Add inactive mines as a separate series below active mines
        if (visibleInactiveData.length > 0) {
          series.push({
            name: 'Inactive Mines',
            type: 'scatter',
            coordinateSystem: 'geo',
            data: visibleInactiveData,
            symbolSize: (val: any) => {
              const metric = Array.isArray(val) ? (val[4] ?? null) : null;
              return metric ?? 12;
            },
            label: { show: false },
            itemStyle: {
              color: inactiveMineColor,
              borderColor: '#FFFFFF',
              borderWidth: 1,
              opacity: inactiveMineOpacity
            },
            zlevel: 0,
            z: 0,
            z2: 0
          });
        }

        if (visibleActiveRepowerData.length > 0) {
          const activeMineIconSize = this.data?.activeMineIconSize ?? 42;
          series.push({
            name: 'Active Repowers',
            type: 'scatter',
            coordinateSystem: 'geo',
            data: visibleActiveRepowerData,
            symbol: this.activeRepowerFireSymbol,
            symbolSize: (val: any, params: any) => {
              const sizeFromPoint = params?.data?.__activeIconSize;
              if (typeof sizeFromPoint === 'number' && Number.isFinite(sizeFromPoint)) {
                return sizeFromPoint;
              }
              const metric = Array.isArray(val) ? (val[4] ?? null) : null;
              const metricSize = this.getBubbleSizeFromMetric(metric, minMetric, maxMetric);
              return metricSize ?? activeMineIconSize;
            },
            label: { show: false },
            itemStyle: {
              borderWidth: 1.5
            },
            emphasis: {
              itemStyle: {
                borderWidth: 2.4
              }
            },
            zlevel: 6,
            z: 2600,
            z2: 2600
          });
        }
      }

      // Tracking max risk per region AND per country for more granular coloring
      const regionMaxRisk: Record<string, number> = {};
      const countryMaxRisk: Record<string, number> = {};

      bubbleData.forEach((b: any) => {
        const region = b.__regionRaw;
        const country = (b.__countryRaw || '').toLowerCase().trim();
        const score = b.__risk?.score || 1;

        if (region) {
          regionMaxRisk[region] = Math.max(regionMaxRisk[region] || 0, score);
        }
        if (country) {
          countryMaxRisk[country] = Math.max(countryMaxRisk[country] || 0, score);
        }
      });

      const regionMappings = this.data?.regionMappings || {};
      const regionColors = this.data?.regionColors || {};
      const selectedMine = this.data?.selectedMine;
      const mineCountry = selectedMine?.__countryRaw || '';
      const inferredRegionFromMine = mineCountry ? this.getRegionForCountry(mineCountry) : null;
      const currentUrl = (this.routerNav?.url || '').toLowerCase();
      const isScreen1 = currentUrl.includes('repower-predictor') && !currentUrl.includes('repower-screen-');
      const filterFocusedRegion = !isScreen1 && selectedRegions.length === 1 ? selectedRegions[0] : null;
      const focusedRegionInput = this.data?.selectedRegion || inferredRegionFromMine || filterFocusedRegion;
      const focusedRegion = this.resolveRegionKey(focusedRegionInput) || focusedRegionInput;
      const isFocusedRegionView = !!focusedRegion;
      const isFilterDrivenUpdate =
        selectedRegions.length > 0 ||
        selectedCountries.length > 0 ||
        selectedMines.length > 0 ||
        selectedOwnerTypes.length > 0;
      const mappingToRender = isFocusedRegionView && focusedRegion
        ? { [focusedRegion]: regionMappings[focusedRegion] || [] }
        : regionMappings;
      const focusedCountries = isFocusedRegionView && focusedRegion
        ? (regionMappings[focusedRegion] || [])
        : selectedCountries;
      const selectedCountryNorm = new Set(
        focusedCountries.map((country: string) => (country || '').toLowerCase().trim())
      );

      // Add a series for Region Labels (e.g., "NACA", "APAC") to match user screenshot
      const regionCenters: Record<string, [number, number]> = {
        'NACA': [-100, 48],
        'LATAM': [-60, -20],
        'AME': [20, 15],
        'APAC': [135, -25],
        'CHINA': [105, 35],
        'EUROPE': [15, 55],
        'Northern Europe': [15, 62],
        'INDIA': [78.9, 22.5],
        'India': [78.9, 22.5]
      };

      const regionLabelData: any[] = [];
      if (!isFocusedRegionView) {
        for (const regionKey of Object.keys(regionMappings)) {
          const center = regionCenters[regionKey];
          if (center) {
            regionLabelData.push({
              name: regionKey,
              value: [center[0], center[1]],
              label: {
                show: true,
                formatter: '{b}',
                position: 'inside',
                backgroundColor: '#fff',
                padding: [6, 12],
                borderRadius: 4,
                color: '#455A68', // Dark slate text to match the regions
                fontWeight: 'bold',
                fontSize: 13,
                shadowColor: 'rgba(0,0,0,0.15)',
                shadowBlur: 4,
                shadowOffsetY: 2
              }
            });
          }
        }
      }

      if (regionLabelData.length > 0) {
        series.push({
          name: 'RegionLabels',
          type: 'scatter',
          coordinateSystem: 'geo',
          silent: true,
          data: regionLabelData,
          symbolSize: 1,
          itemStyle: { opacity: 0 },
          zlevel: 5 // Ensure labels are on top
        });
      }

      const regionsArray: any[] = [];
      for (const regionKey of Object.keys(mappingToRender)) {
        const countries: string[] = mappingToRender[regionKey] || [];
        const color = regionColors[regionKey] || '#95a9b6';

        for (const countryName of countries) {
          regionsArray.push({
            name: countryName,
            itemStyle: {
              areaColor: color,
              borderColor: '#fff', // White border as requested
              borderWidth: 0.8
            },
            emphasis: {
              itemStyle: {
                areaColor: this.darkenColor(color, 10),
                borderColor: '#fff'
              },
              label: { show: false }
            }
          });
        }
      }

      const option: any = {
        animationDurationUpdate: 350,
        animationEasingUpdate: 'cubicOut',
        tooltip: {
          show: true,
          trigger: 'item',
          formatter: (params: any) => {
            console.log('[Tooltip] params:', params);
            if (params.seriesType === 'scatter' || params.seriesType === 'effectScatter') {
              return `<div style="padding: 2px 6px;"><strong>${params.name}</strong></div>`;
            }
            if (params.componentType === 'geo' || params.seriesType === 'map' || params.seriesType === 'world-map') {
              const countryName = (params?.name || params?.data?.name || '').toString();
              if (!countryName) return '';

              // Look up the region this country belongs to
              const region = this.getRegionForCountry(countryName);

              // Only show tooltip if country belongs to a mapped region
              if (!region) return '';

              return `<div style="padding: 2px 6px;"><strong>${region}</strong></div>`;
            }
            return '';
          }
        },
        geo: {
          map: 'world',
          roam: !isStatic,
          top: 10,
          bottom: 30,
          left: 0,
          TooltipComponent: {
            show: true
          },
          right: 0,
          itemStyle: {
            // Even darker base color for unmapped countries as requested
            areaColor: isFocusedRegionView ? 'transparent' : '#b0bdc5',
            borderColor: isFocusedRegionView ? 'transparent' : '#fff',
            borderWidth: 0.8
          },
          emphasis: {
            itemStyle: {
              areaColor: isFocusedRegionView ? 'transparent' : '#a1adba',
              borderColor: isFocusedRegionView ? 'transparent' : '#fff',
              borderWidth: 0.8
            },
            label: { show: false }
          },
          regions: regionsArray
        },
        series: series
      };

      (this as any).chart.setOption(option, {
        notMerge: false,
        replaceMerge: ['series'],
        lazyUpdate: false
      });

      (this as any).chart.hideLoading();
      if (selectedMine) {
        const mineDelay = isFilterDrivenUpdate ? 0 : 100;
        setTimeout(() => this.zoomToSelectedMine(selectedMine), mineDelay);
      }

      if (focusedRegion) {
        const regionDelay = isFilterDrivenUpdate ? 0 : 100;
        setTimeout(() => this.zoomToSelectedRegion(focusedRegion), regionDelay);
      }

      // Remove old click listeners before adding new ones
      (this as any).chart.off('click');

      // Prevent interaction until chart is fully ready
      this.chartReadyForInteraction = false;

      (this as any).chart.on('click', (params: any) => {
        console.log('[CustomWorldMap] Chart click detected:', params, 'chartReadyForInteraction:', this.chartReadyForInteraction);

        // Skip if chart is still initializing
        if (!this.chartReadyForInteraction) {
          console.log('[CustomWorldMap] Chart not ready for interaction, ignoring click');
          return;
        }

        const isBubbleClick =
          params.seriesType === 'scatter' ||
          params.seriesType === 'effectScatter' ||
          params.componentType === 'series';

        // Screen-3 is a read-only view: ignore bubble click interactions.
        if (this.isScreen3View && isBubbleClick) {
          console.log('[CustomWorldMap] Screen-3 bubble click ignored');
          return;
        }

        const navConfig = this.getNavigateTo();
        const regionNavConfig = this.getRegionNavigateTo();

        if (params.componentType === 'geo') {
          const region = this.getRegionForCountry(params.name);
          if (region) {
            if (regionNavConfig) {
              console.log('[CustomWorldMap] Navigating to region:', regionNavConfig);
              this.routerNav.navigate([regionNavConfig], {
                state: {
                  selectedRegion: region,
                  inactiveMinesSnapshot: this.lastInactiveData || [],
                  activeRepowersSnapshot: this.lastActiveRepowerData || [],
                  regionColors: this.data?.regionColors || {},
                  globalMinMetric: this.currentMinMetric,
                  globalMaxMetric: this.currentMaxMetric
                }
              });
            } else {
              this.zoomToRegion(region);
            }
          }
        } else if (isBubbleClick) {
          try {
            const point = params.data;
            if (!point) return;

            let match = null;
            if ((this as any).lastBubbleData) {
              match = (this as any).lastBubbleData.find((b: any) => {
                return b.name && point.name && b.name === point.name;
              });
            }
            if (!match && this.lastInactiveData) {
              match = this.lastInactiveData.find((b: any) => {
                return b.name && point.name && b.name === point.name;
              });
            }
            if (!match && (point.__isInactive || point.__countryRaw)) {
              match = point;
            }

            if (match) {
              // Prevent interaction with inactive mines
              if (match.__isInactive && !this.data?.inactiveMineClickable) {
                console.log('[CustomWorldMap] Inactive mine clicked - interaction blocked');
                return;
              }

              if (navConfig) {
                console.log('[CustomWorldMap] Navigating to mine:', navConfig);
                this.routerNav.navigate([navConfig], {
                  state: {
                    selectedMine: match,
                    regionColors: this.data?.regionColors || {},
                    globalMinMetric: this.currentMinMetric,
                    globalMaxMetric: this.currentMaxMetric
                  }
                });
              } else {
                const countryRaw = match.__countryRaw || '';
                const region = this.getRegionForCountry(countryRaw);
                if (region) {
                  this.zoomToRegion(region, [match]);
                } else {
                  this.zoomToBubble(point);
                }
              }
            }
          } catch (e) {
            console.warn('WorldMap: failed handling bubble click', e);
          }
        }
      });

      // Mark that setup is complete, user clicks are now allowed
      setTimeout(() => {
        this.chartReadyForInteraction = true;
        console.log('[CustomWorldMap] Chart is now ready for user interactions');
      }, 300);

      // Clean up old resize listener before registering new one
      if (this.resizeListener) {
        window.removeEventListener('resize', this.resizeListener);
      }

      this.resizeListener = () => (this as any).chart?.resize();
      window.addEventListener('resize', this.resizeListener);
    }
  }

  protected override getRegionForCountry(countryName: string): string | null {
    if (!countryName) return null;
    const normSearch = countryName.toLowerCase().trim();
    const regionMappings = this.data?.regionMappings || {};

    for (const regionKey of Object.keys(regionMappings)) {
      const countries: string[] = regionMappings[regionKey] || [];
      if (countries.some(c => {
        const normC = c.toLowerCase().trim();
        return normC === normSearch || normSearch.includes(normC) || normC.includes(normSearch);
      })) {
        return regionKey;
      }
    }
    return null;
  }

  private getSelectedRegionsFromFilters(): string[] {
    const requestParams = this.latestFilters?.requestParams || {};
    const regions = this.extractNormalizedFilterValues(requestParams?.region);
    const knownRegions = new Set(
      Object.keys(this.data?.regionMappings || {}).map((key) => key.toLowerCase().trim())
    );

    return regions
      .map((region) => this.resolveRegionKey(region) || region)
      .filter((region) => {
        const normalized = region.toLowerCase().trim();
        if (normalized === 'global' || normalized === 'all') {
          return false;
        }
        // Ignore non-region values accidentally routed through region filters (e.g. owner type values).
        return knownRegions.size === 0 || knownRegions.has(normalized);
      });
  }

  private getSelectedCountriesFromFilters(): string[] {
    const requestParams = this.latestFilters?.requestParams || {};
    return this.extractNormalizedFilterValues(requestParams?.country);
  }

  private getSelectedMinesFromFilters(): string[] {
    const requestParams = this.latestFilters?.requestParams || {};
    return this.extractNormalizedFilterValues(requestParams?.mine);
  }

  private getSelectedOwnerTypesFromFilters(): string[] {
    const ownerTypeAliases = ['owner_type', 'ownerType', 'owner type', 'owner-type', 'ownertype'];
    const globalFilters = this.getCurrentGlobalFilters() || {};
    const candidateSources = [
      this.latestFilters?.requestParams,
      this.latestFilters?.requestData?.filter,
      this.latestFilters?.requestData,
      this.lastApiFilterParams,
      globalFilters?.requestParams,
      globalFilters?.requestData?.filter,
      globalFilters?.requestData,
    ];

    for (const source of candidateSources) {
      const ownerTypeValues = this.getFilterValueByAliases(source, ownerTypeAliases);
      const normalizedValues = this.extractNormalizedFilterValues(ownerTypeValues)
        .map((value) => this.normalizeOwnerType(value))
        .filter((value) => value.length > 0);

      if (normalizedValues.length > 0) {
        return Array.from(new Set(normalizedValues));
      }
    }

    return [];
  }

  private extractNormalizedFilterValues(input: any): string[] {
    const flattened: any[] = [];

    const visit = (value: any): void => {
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }
      if (typeof value === 'object') {
        if ('value' in value) {
          visit((value as any).value);
          return;
        }
        if ('key' in value) {
          visit((value as any).key);
          return;
        }
        if ('label' in value) {
          visit((value as any).label);
          return;
        }
      }
      flattened.push(value);
    };

    visit(input);

    return flattened
      .map((value) => value.toString().trim())
      .filter((value) => value.length > 0);
  }

  private resolveRegionKey(regionName: string | null | undefined): string | null {
    if (!regionName) return null;
    const regionMappings = this.data?.regionMappings || {};
    const candidates = Object.keys(regionMappings);
    if (candidates.length === 0) return regionName;

    const normalized = regionName.toString().trim().toLowerCase();
    const exact = candidates.find((key) => key.toLowerCase().trim() === normalized);
    if (exact) return exact;

    const aliasCandidates: Record<string, string[]> = {
      europe: ['europe', 'northern europe'],
      'northern europe': ['northern europe', 'europe'],
      india: ['india'],
      emea: ['ame'],
    };

    const aliases = aliasCandidates[normalized] || [];
    for (const alias of aliases) {
      const match = candidates.find((key) => key.toLowerCase().trim() === alias);
      if (match) return match;
    }

    if (normalized.includes('europe')) {
      const europeMatch = candidates.find((key) => key.toLowerCase().includes('europe'));
      if (europeMatch) return europeMatch;
    }

    return null;
  }

  private getFallbackCountryForRegion(regionName: string | null | undefined): string | null {
    const resolvedRegion = this.resolveRegionKey(regionName);
    if (!resolvedRegion) return null;
    const regionMappings = this.data?.regionMappings || {};
    const countries: string[] = regionMappings[resolvedRegion] || [];
    for (const country of countries) {
      const center = (this as any).getCountryCenter(country);
      if (center) return country;
    }
    return null;
  }

  private getRegionCenter(regionName: string | null | undefined): [number, number] | null {
    const resolvedRegion = this.resolveRegionKey(regionName);
    const regionCenters: Record<string, [number, number]> = {
      'NACA': [-100, 48],
      'LATAM': [-60, -20],
      'AME': [20, 15],
      'APAC': [135, -25],
      'CHINA': [105, 35],
      'EUROPE': [15, 55],
      'Northern Europe': [15, 62],
      'INDIA': [78.9, 22.5],
      'India': [78.9, 22.5]
    };
    if (!resolvedRegion) return null;
    return regionCenters[resolvedRegion] || null;
  }

  private getCountriesForRegions(selectedRegionNorm: string[]): Set<string> {
    const regionMappings = this.data?.regionMappings || {};
    const countries = new Set<string>();

    Object.keys(regionMappings).forEach((regionKey) => {
      const regionNorm = regionKey.toLowerCase().trim();
      if (!selectedRegionNorm.includes(regionNorm)) return;

      const list: string[] = regionMappings[regionKey] || [];
      list.forEach((country) => countries.add((country || '').toLowerCase().trim()));
    });

    return countries;
  }

  private filterInactiveByRegionNorms(regionNorms: string[], source: any[]): any[] {
    if (!Array.isArray(source) || source.length === 0) return [];
    if (!Array.isArray(regionNorms) || regionNorms.length === 0) return source;

    const mappedCountries = this.getCountriesForRegions(regionNorms);
    return source.filter((bubble) => {
      const bubbleRegion = (bubble?.__regionNorm || '').toLowerCase().trim();
      const bubbleCountry = (bubble?.__countryNorm || '').toLowerCase().trim();
      if (bubbleRegion && regionNorms.includes(bubbleRegion)) {
        return true;
      }
      return mappedCountries.has(bubbleCountry);
    });
  }

  protected override zoomToRegion(regionName: string, specificBubbleData?: any[]): void {
    const resolvedRegionName = this.resolveRegionKey(regionName) || regionName;
    if (!(this as any).chart || !(this as any).regionBounds[resolvedRegionName]) {
      if (!(this as any).chart) {
        setTimeout(() => this.zoomToRegion(resolvedRegionName), 100);
      }
      return;
    }

    const bounds = this.getAdjustedRegionBounds(resolvedRegionName, (this as any).regionBounds[resolvedRegionName]);
    const regionMappings = this.data?.regionMappings || {};
    const regionColors = this.data?.regionColors || {};
    const selectedCountries = regionMappings[resolvedRegionName] || [];
    const selectedColor = regionColors[resolvedRegionName] || '#f4a582';

    const regionsArray = selectedCountries.map((countryName: string) => ({
      name: countryName,
      itemStyle: { areaColor: selectedColor, borderColor: '#ffffff', borderWidth: 0.8 },
      emphasis: { itemStyle: { areaColor: selectedColor, borderColor: '#ffffff', borderWidth: 0.8 }, label: { show: false } }
    }));

    const optionPatch: any = {
      geo: {
        center: bounds.center,
        zoom: bounds.zoom,
        roam: false,
        emphasis: { label: { show: false } },
        itemStyle: {
          areaColor: 'transparent',
          borderColor: 'transparent',
          borderWidth: 0.8
        },
        regions: regionsArray
      }
    };

    try {
      const selectedNorm = selectedCountries.map((c: string) => c.toLowerCase().trim());
      const filtered = specificBubbleData || ((this as any).lastBubbleData || []).filter((b: any) =>
        selectedNorm.includes((b.__countryNorm || '').toLowerCase().trim())
      );
      const filteredInactive = (this.lastInactiveData || []).filter((b: any) =>
        selectedNorm.includes((b.__countryNorm || '').toLowerCase().trim())
      );
      const filteredActiveRepowers = (this.lastActiveRepowerData || []).filter((b: any) =>
        selectedNorm.includes((b.__countryNorm || '').toLowerCase().trim())
      );
      // Keep legend-toggle source data aligned with the currently focused region.
      this.lastVisibleInactiveData = [...filteredInactive];
      this.lastActiveRepowerData = [...filteredActiveRepowers];
      const dynamicBounds = this.getBoundsForVisiblePoints(
        [...filtered, ...filteredInactive, ...filteredActiveRepowers],
        bounds
      );
      optionPatch.geo.center = dynamicBounds.center;
      optionPatch.geo.zoom = dynamicBounds.zoom;

      const currentOpt: any = (this as any).chart.getOption();
      const bubbleSeriesIndex = (this as any).bubbleSeriesIndex;
      if (currentOpt && Array.isArray(currentOpt.series) && bubbleSeriesIndex >= 0 && bubbleSeriesIndex < currentOpt.series.length) {
        const updatedSeries = [...currentOpt.series];
        updatedSeries[bubbleSeriesIndex] = {
          ...updatedSeries[bubbleSeriesIndex],
          data: filtered
        };
        const inactiveSeriesIndex = updatedSeries.findIndex((s: any) => s?.name === 'Inactive Mines');
        if (inactiveSeriesIndex >= 0) {
          updatedSeries[inactiveSeriesIndex] = {
            ...updatedSeries[inactiveSeriesIndex],
            data: filteredInactive
          };
        } else if (filteredInactive.length > 0) {
          const inactiveMineColor = this.data?.inactiveMineColor || '#cbd5e1';
          const inactiveMineOpacity = this.data?.inactiveMineOpacity ?? 0.55;
          const inactiveMineBorderColor = this.data?.inactiveMineBorderColor || '#FFFFFF';
          const inactiveMineBorderWidth = this.data?.inactiveMineBorderWidth ?? 1.5;
          const inactiveMineBubbleSize = this.data?.inactiveMineBubbleSize ?? 25;
          updatedSeries.push({
            name: 'Inactive Mines',
            type: 'scatter',
            coordinateSystem: 'geo',
            data: filteredInactive,
            symbolSize: (val: any) => {
              const metric = Array.isArray(val) ? (val[4] ?? null) : null;
              return metric ?? inactiveMineBubbleSize;
            },
            label: { show: false },
            itemStyle: {
              color: inactiveMineColor,
              borderColor: inactiveMineBorderColor,
              borderWidth: inactiveMineBorderWidth,
              opacity: inactiveMineOpacity
            },
            zlevel: 0,
            z: 0,
            z2: 0
          });
        }
        const activeRepowerSeriesIndex = updatedSeries.findIndex((s: any) => s?.name === 'Active Repowers');
        if (activeRepowerSeriesIndex >= 0) {
          updatedSeries[activeRepowerSeriesIndex] = {
            ...updatedSeries[activeRepowerSeriesIndex],
            data: filteredActiveRepowers
          };
        }
        optionPatch.series = updatedSeries;
      }
    } catch (e) {
      console.warn('WorldMap: failed to prepare bubble filter for region', e);
    }

    (this as any).chart.setOption(optionPatch, {
      notMerge: false,
      replaceMerge: ['series'],
      lazyUpdate: false
    });

    setTimeout(() => {
      (this as any).chart.resize();
    }, 300);
  }

  private getBoundsForVisiblePoints(
    data: any[],
    fallbackBounds: { center: [number, number]; zoom: number }
  ): { center: [number, number]; zoom: number } {
    // If no data or all points are empty, return fallback bounds
    if (!data || data.length === 0) {
      return fallbackBounds;
    }

    // Extract valid coordinates from data points
    const validCoords = data
      .map((point: any) => this.getCoordsFromRow(point))
      .filter((coords: any) => coords !== null) as [number, number][];

    // If no valid coordinates, return fallback bounds
    if (validCoords.length === 0) {
      return fallbackBounds;
    }

    // Calculate bounding box
    const lons = validCoords.map((c) => c[0]);
    const lats = validCoords.map((c) => c[1]);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    // Calculate center and zoom to fit all points
    const center: [number, number] = [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
    const lonDiff = maxLon - minLon;
    const latDiff = maxLat - minLat;
    const maxDiff = Math.max(lonDiff, latDiff);

    // Simple zoom calculation (adjust as needed based on your map projection)
    let zoom = fallbackBounds.zoom;
    if (maxDiff > 0) {
      zoom = Math.max(1, 180 / Math.max(maxDiff, 5));
    }

    return { center, zoom };
  }

  private getAdjustedRegionBounds(regionName: string, bounds: { center: [number, number]; zoom: number }): {
    center: [number, number];
    zoom: number;
  } {
    const regionFocusOverrides = this.data?.regionFocusOverrides;
    const regionOverride = regionFocusOverrides?.[regionName];
    if (regionOverride) {
      const baseCenter = Array.isArray(regionOverride.center) && regionOverride.center.length === 2
        ? [Number(regionOverride.center[0]), Number(regionOverride.center[1])] as [number, number]
        : [bounds.center[0], bounds.center[1]] as [number, number];
      const offsetX = Number(regionOverride.centerOffsetX ?? 0);
      const offsetY = Number(regionOverride.centerOffsetY ?? 0);
      const overrideZoom = Number(regionOverride.zoom);
      return {
        center: [baseCenter[0] + (Number.isFinite(offsetX) ? offsetX : 0), baseCenter[1] + (Number.isFinite(offsetY) ? offsetY : 0)],
        zoom: Number.isFinite(overrideZoom) && overrideZoom > 0 ? overrideZoom : bounds.zoom
      };
    }

    if (this.isScreen2View) {
      if (regionName === 'AME') {
        // Shift the AME viewport slightly south and zoom out a touch so southern bubbles
        // remain visible within the focused region view on screen-2.
        return {
          center: [bounds.center[0], bounds.center[1] - 6],
          zoom: 1.65
        };
      }

      if (regionName === 'LATAM') {
        // Give LATAM more breathing room on the Pacific side and at the southern tip.
        return {
          center: [bounds.center[0] - 4, bounds.center[1] - 3],
          zoom: 1.65
        };
      }
    }

    return bounds;
  }

  protected override zoomToSelectedMine(selectedMine: any): void {
    if (!(this as any).chart || !selectedMine) return;

    if (selectedMine.__isActiveRepower === true) {
      const activeMineNorm = (selectedMine?.name || selectedMine?.__mineRaw || '').toString().toLowerCase().trim();
      const filteredActive = (this.lastActiveRepowerData || []).filter((b: any) =>
        (b?.__mineNorm || '').toLowerCase().trim() === activeMineNorm
      );

      if (filteredActive.length > 0) {
        const currentOpt: any = (this as any).chart.getOption();
        if (currentOpt?.series && Array.isArray(currentOpt.series)) {
          const updatedSeries = [...currentOpt.series];
          const activeRepowerSeriesIndex = updatedSeries.findIndex((s: any) => s?.name === 'Active Repowers');
          const activeSeriesIndex = (this as any).bubbleSeriesIndex;
          const inactiveSeriesIndex = updatedSeries.findIndex((s: any) => s?.name === 'Inactive Mines');

          if (activeRepowerSeriesIndex >= 0) {
            updatedSeries[activeRepowerSeriesIndex] = {
              ...updatedSeries[activeRepowerSeriesIndex],
              data: filteredActive
            };
          }
          if (activeSeriesIndex >= 0) {
            updatedSeries[activeSeriesIndex] = {
              ...updatedSeries[activeSeriesIndex],
              data: []
            };
          }
          if (inactiveSeriesIndex >= 0) {
            updatedSeries[inactiveSeriesIndex] = {
              ...updatedSeries[inactiveSeriesIndex],
              data: []
            };
          }

          (this as any).chart.setOption({ series: updatedSeries }, { notMerge: false, lazyUpdate: false });
        }
      }
    }

    const countryRaw = selectedMine.__countryRaw || '';
    const region = this.getRegionForCountry(countryRaw);

    if (region) {
      const selectedNorm = (selectedMine.name || '').toLowerCase().trim();
      const filtered = ((this as any).lastBubbleData || []).filter((b: any) =>
        (b.name || '').toLowerCase().trim() === selectedNorm
      );
      this.zoomToRegion(region, filtered.length > 0 ? filtered : [selectedMine]);
    } else {
      const coords = this.getCoordsFromPoint(selectedMine);
      if (coords) {
        const [lon, lat] = coords;
        (this as any).chart.setOption({ geo: { center: [lon, lat], zoom: 6, roam: false } });
        this.filterSeriesTo([selectedMine]);
      }
    }
  }

  protected override filterSeriesTo(data: any[]) {
    const chart = (this as any).chart;
    const bubbleSeriesIndex = (this as any).bubbleSeriesIndex;
    if (!chart) return;

    const currentOpt: any = chart.getOption();
    if (currentOpt && Array.isArray(currentOpt.series) && bubbleSeriesIndex >= 0 && bubbleSeriesIndex < currentOpt.series.length) {
      currentOpt.series[bubbleSeriesIndex].data = data;
      chart.setOption({ series: currentOpt.series }, { notMerge: false, lazyUpdate: false });
      return;
    }

    chart.setOption({ series: [{ data }] }, { notMerge: false, lazyUpdate: false });
  }

  protected zoomToSelectedRegion(regionName: string): void {
    this.zoomToRegion(regionName);
  }

  public get selectedContinentLabel(): string | null {
    // Only show on repower-screen-2 as requested by the user
    if (!this.routerNav || !this.routerNav.url.includes('repower-screen-2')) {
      return null;
    }

    // If a region is explicitly set in the component configuration
    if (this.data?.selectedRegion) {
      return this.data.selectedRegion;
    }

    // Try to extract a single selected region from active filters
    const regions = this.getSelectedRegionsFromFilters();
    if (regions && regions.length === 1) {
      return regions[0];
    }

    return null;
  }

  public get isScreen1View(): boolean {
    const url = (this.routerNav?.url || '').toLowerCase();
    return url.includes('repower-predictor') && !url.includes('repower-screen-');
  }

  public get isScreen2View(): boolean {
    const url = (this.routerNav?.url || '').toLowerCase();
    return url.includes('repower-screen-2');
  }

  public get isScreen3View(): boolean {
    const url = (this.routerNav?.url || '').toLowerCase();
    return url.includes('repower-screen-3');
  }

  public get showRiskLegend(): boolean {
    // Show Repower Risk legend on both the main predictor screen and screen-2.
    return this.isScreen1View || this.isScreen2View;
  }

  public toggleRiskFilter(riskScore: 'high' | 'medium' | 'low' | 'inactive' | 'active'): void {
    if (this.activeRiskFilter === riskScore) {
      this.activeRiskFilter = null;
    } else {
      this.activeRiskFilter = riskScore;
    }
    this.applyRiskFilter();
  }

  private applyRiskFilter(): void {
    const allActiveData = (this as any).lastBubbleData || [];
    const allActiveRepowerData = this.lastActiveRepowerData || [];
    const scopedInactiveData = this.getScopedInactiveDataForCurrentSelection();
    const chart = (this as any).chart;
    if (!chart) return;

    if (this.activeRiskFilter === null) {
      // Restore both
      const option = chart.getOption();
      const activeSeriesIndex = (this as any).bubbleSeriesIndex;
      const inactiveSeries = option.series.find((s: any) => s.name === 'Inactive Mines');
      const activeRepowerSeries = option.series.find((s: any) => s.name === 'Active Repowers');

      if (activeSeriesIndex >= 0) {
        option.series[activeSeriesIndex].data = allActiveData;
      }
      if (inactiveSeries) {
        inactiveSeries.data = scopedInactiveData;
      }
      if (activeRepowerSeries) {
        activeRepowerSeries.data = allActiveRepowerData;
      }
      chart.setOption(option, { notMerge: false });
    } else if (this.activeRiskFilter === 'active') {
      this.showOnlyActiveRepowers();
    } else if (this.activeRiskFilter === 'inactive') {
      this.showOnlyInactive();
    } else {
      const bucket = this.activeRiskFilter;
      const filtered = allActiveData.filter((b: any) =>
        this.getRiskBucket(this.toNumber(b.__risk?.score)) === bucket
      );

      const option = chart.getOption();
      const activeSeriesIndex = (this as any).bubbleSeriesIndex;
      const inactiveSeries = option.series.find((s: any) => s.name === 'Inactive Mines');
      const activeRepowerSeries = option.series.find((s: any) => s.name === 'Active Repowers');

      if (activeSeriesIndex >= 0) {
        option.series[activeSeriesIndex].data = filtered;
      }
      if (inactiveSeries) {
        inactiveSeries.data = [];
      }
      if (activeRepowerSeries) {
        activeRepowerSeries.data = [];
      }
      chart.setOption(option, { notMerge: false });
    }
  }

  private showOnlyInactive(): void {
    const chart = (this as any).chart;
    if (!chart) return;
    const option = chart.getOption();
    if (!option || !option.series) return;
    const scopedInactiveData = this.getScopedInactiveDataForCurrentSelection();

    const activeSeriesIndex = (this as any).bubbleSeriesIndex;
    const inactiveSeries = option.series.find((s: any) => s.name === 'Inactive Mines');
    const activeRepowerSeries = option.series.find((s: any) => s.name === 'Active Repowers');

    if (activeSeriesIndex >= 0) {
      option.series[activeSeriesIndex].data = [];
    }

    if (inactiveSeries) {
      inactiveSeries.data = scopedInactiveData;
    }
    if (activeRepowerSeries) {
      activeRepowerSeries.data = [];
    }

    chart.setOption(option, { notMerge: false });
  }

  private showOnlyActiveRepowers(): void {
    const chart = (this as any).chart;
    if (!chart) return;
    const option = chart.getOption();
    if (!option || !option.series) return;

    const activeSeriesIndex = (this as any).bubbleSeriesIndex;
    const inactiveSeries = option.series.find((s: any) => s.name === 'Inactive Mines');
    const activeRepowerSeries = option.series.find((s: any) => s.name === 'Active Repowers');

    if (activeSeriesIndex >= 0) {
      option.series[activeSeriesIndex].data = [];
    }
    if (inactiveSeries) {
      inactiveSeries.data = [];
    }
    if (activeRepowerSeries) {
      activeRepowerSeries.data = this.lastActiveRepowerData;
    }

    chart.setOption(option, { notMerge: false });
  }

  private refreshMapWithFilters(): void {
    this.fetchData(this.latestFilters);
  }

  private getScopedInactiveDataForCurrentSelection(): any[] {
    const baseInactiveData = Array.isArray(this.lastVisibleInactiveData) && this.lastVisibleInactiveData.length > 0
      ? this.lastVisibleInactiveData
      : (this.lastInactiveData || []);

    if (!Array.isArray(baseInactiveData) || baseInactiveData.length === 0) {
      return [];
    }

    let scoped = [...baseInactiveData];

    const selectedRegions = this.getSelectedRegionsFromFilters();
    const focusedRegionFromState = this.resolveRegionKey(this.data?.selectedRegion) || this.data?.selectedRegion;
    const effectiveRegions = selectedRegions.length > 0
      ? selectedRegions
      : (focusedRegionFromState ? [focusedRegionFromState] : []);

    if (effectiveRegions.length > 0) {
      const selectedRegionNorm = effectiveRegions.map((r) => r.toLowerCase().trim());
      scoped = this.filterInactiveByRegionNorms(selectedRegionNorm, scoped);
    }

    const selectedCountries = this.getSelectedCountriesFromFilters();
    if (selectedCountries.length > 0) {
      const selectedCountryNorm = selectedCountries.map((country) => country.toLowerCase().trim());
      scoped = scoped.filter((bubble) => {
        const bubbleCountry = (bubble?.__countryNorm || '').toLowerCase().trim();
        return selectedCountryNorm.includes(bubbleCountry);
      });
    }

    const selectedMines = this.getSelectedMinesFromFilters();
    if (selectedMines.length > 0) {
      const selectedMineNorm = selectedMines.map((mine) => mine.toLowerCase().trim());
      scoped = scoped.filter((bubble) => {
        const bubbleMine = (bubble?.__mineNorm || bubble?.name || '').toLowerCase().trim();
        return selectedMineNorm.includes(bubbleMine);
      });
    }

    return scoped;
  }
}
